import React, { useState, useEffect } from "react";
import { useTheme, setTheme } from "../../lib/theme";
import { setTimePref } from "../../lib/timePrefs";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Share, PlusSquare, MoreVertical, Bell } from "lucide-react";
import { toast } from "sonner";
import { api, getAccount, getTeams, getLegal, updateAccount, acceptTerms } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { useHeightUnit } from "../../lib/units";
import { useFavourite, multiOf } from "../../lib/useFavourite";
import { promptPush, getPushState } from "../../lib/onesignal";
import { PWHL_CONFERENCES } from "../../lib/pwhlConferences";
import { useNotifPick } from "../../lib/useNotifPick";
import { useDeviceView } from "../../lib/DeviceViewContext";
import { orderTeams } from "../../lib/teamMap";
import { TeamBadge } from "../TeamBadge";
import { AuthShell } from "../AuthShell";
import { FollowedLeagues } from "./FollowedLeagues";
import { Loader } from "../Loader";

// Terms v2 is required ONCE from every signed-in account (new + existing). Nothing else is forced.
const TERMS_KEY = "terms_v2";
const isDesktopVp = () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
const isStandalone = () => typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const setupSteps = (profile, desktop = isDesktopVp()) => {
    const d = profile?.setup_done || {};
    return [
        ["terms", !!d[TERMS_KEY]],
        ["favs", !!d.favs], ["notifs", !!d.notifs], ["prefs", !!d.prefs],
        desktop ? ["monitor", !!d.monitor] : ["install", !!d.install || isStandalone()],
    ];
};
export const setupRemaining = (profile) => setupSteps(profile).filter(([, ok]) => !ok).length;
export const termsPending = (profile) => !!profile && !(profile.setup_done || {})[TERMS_KEY];
export const SETUP_STEP_LABELS = ["Sign in", ...setupSteps({}).map(([k]) => k)];

const TITLES = { terms: "Terms & Privacy", favs: "Your teams", notifs: "Game alerts", prefs: "Display & Theme", monitor: "Your screen", install: "Add to Home Screen" };
export const Pill = ({ on, onClick, children, testid }) => (
    <button type="button" onClick={onClick} data-testid={testid}
        className={`flex-1 px-3 py-2.5 rounded-xl border text-xs font-black uppercase tracking-wider transition-colors ${on ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-black/30 border-white/10 text-white/70 hover:text-white hover:border-white/30"}`}>{children}</button>
);

const useIntlTeams = () => useQuery({ queryKey: ["intl-teams"], queryFn: () => api.get("/intl/teams").then((r) => r.data), staleTime: 60 * 60 * 1000 }).data?.teams || [];
const useNcaaTeamList = (on = true) => useQuery({ queryKey: ["ncaa-teams"], queryFn: () => api.get("/ncaaw/teams").then((r) => r.data), staleTime: 6 * 60 * 60 * 1000, enabled: on }).data || [];

export function TeamGrid({ league, isOn, onPick, testPrefix }) {
    const { data: pwhl = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const { data: nhl = [] } = useQuery({ queryKey: ["nhl-teams"], queryFn: () => api.get("/nhl/teams").then((r) => r.data), staleTime: 60 * 60 * 1000 });
    const intl = useIntlTeams();
    const ncaa = useNcaaTeamList(league === "ncaa");
    const items = league === "nhl"
        ? nhl.map((t) => ({ id: t.abbr, name: t.name, node: <img src={t.logo_url} alt={t.abbr} className="h-7 w-7 object-contain" /> }))
        : league === "ncaa"
        ? ncaa.map((t) => ({ id: t.id, name: t.name, node: <img src={t.logo_url} alt={t.abbr} className="h-7 w-7 object-contain" /> }))
        : league === "international"
        ? intl.map((t) => ({ id: t.code, name: t.name, node: <img src={`https://flagcdn.com/w80/${t.iso}.png`} alt={t.code} className="h-5 w-8 object-cover rounded-sm" /> }))
        : orderTeams(pwhl).map((t) => ({ id: t.id, name: t.name, node: <TeamBadge teamId={t.id} size="sm" asLink={false} /> }));
    return (
        <div className={`grid gap-1.5 ${league === "nhl" || league === "ncaa" ? "grid-cols-8" : league === "international" ? "grid-cols-5" : "grid-cols-4"}`} data-testid={`${testPrefix}-grid`}>
            {items.map((t) => {
                const on = isOn(t.id);
                return (
                    <button key={t.id} type="button" title={t.name} onClick={() => onPick(t.id)} data-testid={`${testPrefix}-${t.id}`}
                        className={`relative aspect-square rounded-lg border flex items-center justify-center transition-[opacity,background-color,border-color] ${on ? "bg-[#9D4CDD]/25 border-[#9D4CDD]" : "bg-black/30 border-white/10 opacity-50 grayscale hover:opacity-100 hover:grayscale-0"}`}>
                        {t.node}
                        {on && <Check className="absolute -top-1 -right-1 h-3.5 w-3.5 p-0.5 rounded-full bg-[#22C55E] text-black" strokeWidth={4} />}
                    </button>
                );
            })}
        </div>
    );
}

// [label, groupKey, ids] quick-pick groups per league: All teams · Conferences · (NHL) Divisions.
export function useTeamGroups() {
    const { data: pwhl = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const { data: nhl = [] } = useQuery({ queryKey: ["nhl-teams"], queryFn: () => api.get("/nhl/teams").then((r) => r.data), staleTime: 60 * 60 * 1000 });
    const pw = orderTeams(pwhl).map((t) => t.id);
    const intl = useIntlTeams();
    const ncaa = useNcaaTeamList();
    return {
        ncaa: [["All teams", "all", ncaa.map((t) => t.id)], ...[...new Set(ncaa.map((t) => t.conf))].map((c) => [c, `conf:${c}`, ncaa.filter((t) => t.conf === c).map((t) => t.id)])],
        international: [["All teams", "all", intl.map((t) => t.code)], ...[...new Set(intl.map((t) => t.group).filter(Boolean))].map((g) => [`Group ${g}`, `grp:${g}`, intl.filter((t) => t.group === g).map((t) => t.code)])],
        pwhl: [["All teams", "all", pw], ...Object.entries(PWHL_CONFERENCES).map(([c, ids]) => [c, `conf:${c}`, ids.filter((id) => pw.includes(id))])],
        nhl: [["All teams", "all", nhl.map((t) => t.abbr)],
            ...["Eastern", "Western"].map((c) => [c, `conf:${c}`, nhl.filter((t) => t.conference === c).map((t) => t.abbr)]),
            ...["Atlantic", "Metropolitan", "Central", "Pacific"].map((d) => [d, `div:${d}`, nhl.filter((t) => t.division === d).map((t) => t.abbr)])],
    };
}
export const GroupPills = ({ groups, isOn, onSet, testid }) => (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {groups.map(([label, k, ids]) => {
            const on = ids.length > 0 && ids.every(isOn);
            return (
                <button key={k} type="button" onClick={() => onSet(ids, !on)} data-testid={`${testid}-${k.replace(":", "-").toLowerCase()}`}
                    className={`px-2.5 py-1.5 rounded-lg border text-[10px] font-black uppercase tracking-wider transition-colors ${on ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "bg-black/30 border-white/10 text-white/70 hover:text-white hover:border-white/30"}`}>{label}</button>
            );
        })}
    </div>
);

export function AllowNotifications() {
    const [st, setSt] = useState(null);
    useEffect(() => { Promise.resolve(getPushState()).then(setSt).catch(() => setSt(null)); }, []);
    const on = st?.optedIn && st?.permission !== "denied";
    const allow = async () => {
        const ok = await promptPush();
        const ns = await Promise.resolve(getPushState()).catch(() => null);
        setSt(ns);
        if (ok) toast.success("Notifications allowed on this device");
        else toast.error(ns?.permission === "denied" ? "Notifications are blocked in your browser settings" : "Couldn't enable notifications on this device");
    };
    return (
        <button type="button" onClick={allow} disabled={on} data-testid="setup-allow-notifications"
            className={`w-full flex items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-black uppercase tracking-wider transition-colors ${on ? "bg-[#22C55E]/15 border-[#22C55E]/50 text-[#86EFAC]" : "bg-black/30 border-white/10 text-white/80 hover:text-white hover:border-white/30"}`}>
            <Bell className="h-4 w-4" /> {on ? "Notifications allowed" : "Allow notifications"}
        </button>
    );
}

export const LeagueSwitch = ({ league, setLeague, testid, leagues = [["pwhl", "PWHL"], ["nhl", "NHL"]] }) => (
    <div className="flex gap-2">{leagues.map(([v, l]) => <Pill key={v} on={league === v} testid={`${testid}-${v}`} onClick={() => setLeague(v)}>{l}</Pill>)}</div>
);

// Terms step body — shared by Setup and the site-wide LegalGate (announced policy changes).
export const TermsStepBody = ({ legal, agree, setAgree }) => (<>
    <div className="max-h-[36vh] overflow-y-auto rounded-xl border border-white/10 bg-black/30 px-3.5 py-3 text-xs text-white/70 leading-relaxed space-y-3" data-testid="setup-terms-text">
        {legal?.terms_html ? <div dangerouslySetInnerHTML={{ __html: legal.terms_html }} /> : <div><b className="text-white">Terms of Service</b>{(legal?.terms?.lines || []).map((l, i) => <p key={i}>{l?.text ?? l}</p>)}</div>}
        {legal?.privacy_html ? <div dangerouslySetInnerHTML={{ __html: legal.privacy_html }} /> : (legal?.privacy?.lines || []).length > 0 && <div><b className="text-white">Privacy Policy</b>{legal.privacy.lines.map((l, i) => <p key={i}>{l?.text ?? l}</p>)}</div>}
        <div><b className="text-white">Cookies</b><p>Rosterly uses essential cookies and local storage to keep you signed in and remember your settings. No advertising cookies.</p></div>
    </div>
    <button type="button" onClick={() => setAgree(!agree)} data-testid="setup-terms-checkbox"
        className={`w-full flex items-start gap-3 text-left rounded-xl border-2 px-3.5 py-3 transition-colors ${agree ? "border-[#9D4CDD] bg-[#9D4CDD]/10" : "border-white/10 bg-black/30"}`}>
        <span className={`mt-0.5 h-5 w-5 shrink-0 rounded-md border-2 flex items-center justify-center ${agree ? "bg-[#9D4CDD] border-[#9D4CDD]" : "border-white/30"}`}>{agree && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}</span>
        <span className="text-sm text-white/85">I agree to the Terms of Service, Privacy Policy and the use of cookies.</span>
    </button>
</>);

// Sign In → Setup: one card, one step at a time. Only Terms is required; every other step can be skipped.
export default function UserSetup() {
    const { user, loading, isOwner } = useAuth();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const { data: account } = useQuery({ queryKey: ["account"], queryFn: getAccount, enabled: !!user });
    const { data: legal } = useQuery({ queryKey: ["legal"], queryFn: getLegal, staleTime: 5 * 60 * 1000 });
    const [idx, setIdx] = useState(null);
    const [agree, setAgree] = useState(false);
    const [league, setLeague] = useState("pwhl");
    const [teamSel, setTeam] = useState(null);
    const [follow, setFollow] = useState(null);
    const [favRaw, setFavRaw] = useState({ pwhl: null, nhl: null });
    const [alertLg, setAlertLg] = useState("pwhl");
    const [unit, setUnit] = useHeightUnit();
    const [clock, setClock] = useState(() => { try { return localStorage.getItem("rosterly_time_format") || "12h"; } catch { return "12h"; } });
    const [monitor, setMonitor] = useState("auto");
    const theme = useTheme();
    const { getFavourite, setFavourite } = useFavourite();
    const notif = useNotifPick();
    const { setPcRes } = useDeviceView();
    // Owner "Test setup": every step shown from the start; nothing is saved.
    const [sp] = useSearchParams();
    const preview = sp.get("preview") === "1" && user?.role === "owner";
    const steps = setupSteps(preview ? {} : account?.profile);
    const groups = useTeamGroups();

    useEffect(() => {
        if (!account?.profile || idx !== null) return;
        const p = account.profile;
        const lg = p.fav_league === "nhl" ? "nhl" : "pwhl";
        setLeague(lg); setAlertLg(lg);
        setFavRaw({ pwhl: getFavourite("pwhl"), nhl: getFavourite("nhl") });
        if (p.time_format) setClock(p.time_format);
        if (p.monitor) setMonitor(p.monitor);
        const first = preview ? 0 : steps.findIndex(([, ok]) => !ok);
        setIdx(first < 0 ? steps.length : first);
    }, [account]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <Loader />;
    if (!user) return <Navigate to="/login" replace />;
    if (!account || idx === null) return <Loader />;
    if (idx >= steps.length) return <Navigate to="/" replace />;

    const key = steps[idx][0];
    // Existing favourites (single, multi:, conf:, div:) → selected ids, so they show highlighted.
    const favIds = (lg) => { const v = favRaw[lg]; if (!v) return []; const g = groups[lg].find(([, k]) => k === v); return g ? g[2] : multiOf(v); };
    const team = teamSel || { pwhl: favIds("pwhl"), nhl: favIds("nhl"), international: account?.profile?.fav_teams?.international || [], ncaa: account?.profile?.fav_teams?.ncaa || [] };
    const setFavMany = (ids, on) => setTeam({ ...team, [league]: on ? Array.from(new Set([...team[league], ...ids])) : team[league].filter((x) => !ids.includes(x)) });
    const finish = async (extra = {}) => {
        if (!preview) await updateAccount({ setup_done: { [key === "terms" ? TERMS_KEY : key]: true, [key]: true }, ...extra }).catch(() => { });
        if (idx + 1 >= steps.length) {
            if (preview) { toast.success("Setup preview finished — nothing was saved"); navigate("/account", { replace: true }); return; }
            await qc.refetchQueries({ queryKey: ["account"] });
            toast.success("You're all set"); navigate("/", { replace: true }); return;
        }
        qc.invalidateQueries({ queryKey: ["account"] });
        setIdx(idx + 1);
    };

    const ACTIONS = {
        terms: { ok: agree, run: () => {
            if (preview) { finish(); return; }
            const v = legal?.version || 1;
            try { localStorage.setItem(`rosterly_legal_v${v}`, "yes"); localStorage.setItem("rosterly_tos_agreed", "yes"); localStorage.setItem("rosterly_cookie_ack", "yes"); } catch { /* noop */ }
            acceptTerms(v).catch(() => { });
            finish();
        } },
        favs: { ok: team.pwhl.length + team.nhl.length + team.international.length + team.ncaa.length > 0, run: () => {
            if (preview) { finish(); return; }
            const enc = (lg) => {
                const a = team[lg];
                if (!a.length) return null;
                const g = groups[lg].find(([, k, ids]) => k !== "all" && ids.length === a.length && ids.every((x) => a.includes(x)));
                return g ? g[1] : a.length === 1 ? a[0] : `multi:${a.join(",")}`;
            };
            setFavourite("pwhl", enc("pwhl")); setFavourite("nhl", enc("nhl"));
            finish({ fav_league: team.pwhl.length ? "pwhl" : team.nhl.length ? "nhl" : team.international.length ? "international" : "ncaa", fav_teams: { pwhl: team.pwhl, nhl: team.nhl, international: team.international, ncaa: team.ncaa }, ...(follow ? { followed_leagues: follow } : {}) });
        } },
        notifs: { ok: true, run: () => finish() },
        prefs: { ok: true, run: () => { if (preview) { finish(); return; } setTimePref("time_format", clock); finish({ time_format: clock }); } },
        monitor: { ok: true, run: () => { if (preview) { finish(); return; } setPcRes("auto"); finish({ monitor: "auto" }); } },
        install: { ok: true, run: () => finish() },
    };
    const act = ACTIONS[key];
    const ios = isIos();

    return (
        <AuthShell steps={SETUP_STEP_LABELS} current={idx + 1} back={false} testId="user-setup-page">
            <div className="text-center">
                {preview && <div className="mb-1 text-[9px] font-black uppercase tracking-[0.2em] text-[#F6C453]" data-testid="setup-preview-tag">Owner preview · nothing is saved</div>}
                <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#9D4CDD]" data-testid="setup-progress">Step {idx + 2} of {steps.length + 1}</div>
                <h1 className="font-heading text-2xl font-black uppercase tracking-tight mt-1" data-testid="setup-title">{TITLES[key]}</h1>
            </div>
            <div data-testid={`setup-step-${key}`} className="space-y-3">
                {key === "terms" && <TermsStepBody legal={legal} agree={agree} setAgree={setAgree} />}
                {key === "favs" && (<>
                    <p className="text-xs text-white/50 text-center">Pick as many PWHL, NHL <b>and</b> National teams as you like.</p>
                    <FollowedLeagues value={follow || account?.profile?.followed_leagues || ["pwhl", "nhl", "international", ...(isOwner ? ["ncaa"] : [])]} onChange={setFollow} testid="setup-followed" />
                    <LeagueSwitch league={league} setLeague={setLeague} testid="setup-league" leagues={[["pwhl", "PWHL"], ["nhl", "NHL"], ["international", "National Teams"], ...(isOwner ? [["ncaa", "NCAA"]] : [])]} />
                    <GroupPills groups={groups[league]} isOn={(id) => team[league].includes(id)} onSet={setFavMany} testid="setup-fav-group" />
                    <TeamGrid league={league} isOn={(id) => team[league].includes(id)} testPrefix="setup-fav"
                        onPick={(id) => setFavMany([id], !team[league].includes(id))} />
                    <div className="text-[10px] font-bold text-white/40 text-center" data-testid="setup-fav-count">{team.pwhl.length} PWHL · {team.nhl.length} NHL · {team.international.length} National{isOwner ? ` · ${team.ncaa.length} NCAA` : ""} selected</div>
                </>)}
                {key === "notifs" && (<>
                    <AllowNotifications />
                    <p className="text-xs text-white/50 text-center">Optional — tap any teams you want goal & game alerts for. Nothing is switched on unless you tap it.</p>
                    <LeagueSwitch league={alertLg} setLeague={setAlertLg} testid="setup-alert-league" />
                    <GroupPills groups={groups[alertLg]} isOn={(id) => notif.isOn(alertLg, id)} onSet={(ids, on) => notif.setMany(alertLg, ids, on)} testid="setup-alert-group" />
                    <TeamGrid league={alertLg} isOn={(id) => notif.isOn(alertLg, id)} onPick={(id) => notif.toggle(alertLg, id)} testPrefix="setup-alert" />
                </>)}
                {key === "prefs" && (<>
                    <div className="text-[10px] font-black uppercase tracking-wider text-white/50">Height</div>
                    <div className="flex gap-2">{[["cm", "cm"], ["ft", "ft / in"]].map(([v, l]) => <Pill key={v} on={unit === v} testid={`setup-height-${v}`} onClick={() => setUnit(v)}>{l}</Pill>)}</div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-white/50">Clock</div>
                    <div className="flex gap-2">{["12h", "24h"].map((v) => <Pill key={v} on={clock === v} testid={`setup-time-${v}`} onClick={() => setClock(v)}>{v}</Pill>)}</div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-white/50">Theme</div>
                    <div className="flex gap-2">{[["light", "Light"], ["dark", "Dark"]].map(([v, l]) => <Pill key={v} on={theme === v} testid={`setup-theme-${v}`} onClick={() => setTheme(v)}>{l}</Pill>)}</div>
                </>)}
                {key === "monitor" && (
                    <div className="flex gap-2">{[["auto", "Auto"], ["1080", "1080p"], ["1440", "1440p"], ["4k", "4K"]].map(([v, l]) => <Pill key={v} on={monitor === v} testid={`setup-monitor-${v}`} onClick={() => setMonitor(v)}>{l}</Pill>)}</div>
                )}
                {key === "install" && (
                    <ol className="space-y-2 text-sm text-white/85" data-testid="setup-install-steps">
                        {(ios ? [
                            [<Share key="i" className="h-5 w-5 text-[#3B82F6]" />, <>Tap <b>Share</b> at the bottom of Safari</>],
                            [<PlusSquare key="i" className="h-5 w-5" />, <>Scroll down, tap <b>Add to Home Screen</b></>],
                            [<Check key="i" className="h-5 w-5 text-[#22C55E]" />, <>Tap <b>Add</b> — open Rosterly from your Home Screen</>],
                        ] : [
                            [<MoreVertical key="i" className="h-5 w-5" />, <>Tap the <b>⋮ menu</b> in your browser</>],
                            [<PlusSquare key="i" className="h-5 w-5" />, <>Tap <b>Install app</b> / <b>Add to Home screen</b></>],
                            [<Check key="i" className="h-5 w-5 text-[#22C55E]" />, <>Tap <b>Install</b> — open Rosterly from your Home Screen</>],
                        ]).map(([icon, txt], i) => (
                            <li key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5">
                                <span className="h-6 w-6 shrink-0 rounded-full bg-white/10 text-[11px] font-black flex items-center justify-center">{i + 1}</span>{icon}<span>{txt}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
            <button type="button" disabled={!act.ok} onClick={act.run} data-testid="setup-next"
                className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed">
                {key === "install" ? "I've added it" : idx + 1 >= steps.length ? "Finish" : "Next"}
            </button>
            {key !== "terms" && (
                <button type="button" onClick={() => (idx + 1 >= steps.length ? finish() : setIdx(idx + 1))} data-testid="setup-skip"
                    className="w-full text-center text-xs font-bold uppercase tracking-wider text-white/40 hover:text-white">Skip</button>
            )}
        </AuthShell>
    );
}
